import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 테마를 따르는 전역 툴팁.
 * 브라우저 기본 title 툴팁은 OS 테마를 따라 다크 모드에서 어색하므로, 마우스가 [title] 요소에 올라가면
 * title 을 data-tip 으로 옮겨(기본 툴팁 억제) 같은 내용을 앱 스타일(.of-tooltip, 다크 대응)로 표시한다.
 * 기존 코드의 title 속성을 바꾸지 않아도 전부 적용된다 (React Flow 컨트롤 버튼 포함).
 */
const SHOW_DELAY = 350;

const TooltipLayer: React.FC = () => {
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    let current: HTMLElement | null = null;

    const hide = () => { if (timer) window.clearTimeout(timer); timer = undefined; current = null; setTip(null); };

    const onOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.('[title], [data-tip]') as HTMLElement | null;
      if (!target || target === current) return;
      hide();
      const title = target.getAttribute('title');
      if (title) { target.setAttribute('data-tip', title); target.removeAttribute('title'); }
      const text = target.getAttribute('data-tip');
      if (!text) return;
      current = target;
      timer = window.setTimeout(() => {
        const r = target.getBoundingClientRect();
        setTip({ text, x: r.left + r.width / 2, y: r.bottom + 6 });
      }, SHOW_DELAY);
    };
    const onOut = (e: MouseEvent) => {
      if (!current) return;
      const to = e.relatedTarget as Node | null;
      if (to && current.contains(to)) return;
      hide();
    };
    document.addEventListener('mouseover', onOver, true);
    document.addEventListener('mouseout', onOut, true);
    document.addEventListener('mousedown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseover', onOver, true);
      document.removeEventListener('mouseout', onOut, true);
      document.removeEventListener('mousedown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  if (!tip) return null;
  const maxW = 360;
  const left = Math.max(8, Math.min(tip.x, window.innerWidth - 8));
  const flipUp = tip.y + 60 > window.innerHeight;
  return createPortal(
    <div
      className="of-tooltip"
      role="tooltip"
      style={{ left, top: flipUp ? undefined : tip.y, bottom: flipUp ? window.innerHeight - tip.y + 32 : undefined, maxWidth: maxW }}
    >
      {tip.text}
    </div>,
    document.body,
  );
};

export default TooltipLayer;
